/**
 * Live preview for the branding editor. Mounts the REAL widget renderer
 * (@widget, the same code installers' sites run) inside a mock installer website,
 * with a mock API so every step can be clicked through.
 *
 *  • Inline widgets render inside the page content, where the snippet's <div> goes.
 *  • Floating widgets are pinned to the preview "window" (not the console page).
 *  • Desktop/Phone switch the preview width; the step bar jumps to any screen.
 *  • Solar estimates (product "solar", docs/12-solar-roof-scan.md §9.4) mount the
 *    real solar widget too: mountWidget routes product "solar" to it. Its roof,
 *    location and result come from the signed-in console API (solarPreview.ts:
 *    the solar engine runs server-side only, never in public JS). Its map needs the
 *    Maps JavaScript browser key: VITE_GOOGLE_MAPS_BROWSER_KEY (may be unset, then
 *    the widget shows the map without a key / its own fallback).
 */
import { useEffect, useRef, useState } from "react";
import { Monitor, RotateCcw, Smartphone } from "lucide-react";
import {
  CONTACT_STEPS,
  FULL_STEPS,
  SOLAR_STEPS,
  SOLAR_STEP_LABELS,
  STEPS,
  STEP_LABELS,
  createMockApi,
  mountWidget,
  normalisePricing,
  type ContactStep,
  type FullStep,
  type MountOptions,
  type SolarStep,
  type Step,
  type WidgetBranding,
  type WidgetHandle,
  type WidgetPricing,
  type WidgetProduct,
} from "@widget";
import { useApi } from "../api";
import { solarPreviewBackend } from "../solarPreview";

/** Any step of any widget the preview can show. */
type AnyStep = Step | FullStep | ContactStep | SolarStep;

// The Maps JavaScript browser key for the solar preview's satellite map (undefined when not set)
const MAPS_KEY: string | undefined = import.meta.env.VITE_GOOGLE_MAPS_BROWSER_KEY || undefined;

// Step names for the Full widget's step bar
const FULL_LABELS: Record<FullStep, string> = {
  location: "Location",
  epc: "EPC",
  property: "Property",
  grant: "Grant",
  estimate: "Estimate",
  contact: "Contact",
  done: "Thank you",
};

type Device = "desktop" | "phone";

export function WidgetPreview({
  branding,
  pricing,
  product = "ashp",
  widgetId,
}: {
  branding: WidgetBranding;
  pricing?: WidgetPricing | null;
  /** Solar estimates mount the solar widget (pass widget.product). */
  product?: WidgetProduct;
  /** Solar: the widget whose roof and prices the preview asks the console API for. */
  widgetId?: string;
}) {
  const [device, setDevice] = useState<Device>("desktop");
  const [step, setStep] = useState<AnyStep>("postcode");
  const [mountKey, setMountKey] = useState(0);
  const hostRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<WidgetHandle | null>(null);
  const brandingRef = useRef(branding);
  brandingRef.current = branding;
  // Quotes in the preview use this widget's saved prices (sample prices until set)
  const pricingRef = useRef(normalisePricing(pricing));
  pricingRef.current = normalisePricing(pricing);
  // Solar answers come from the console API for this widget (its saved prices)
  const consoleApi = useApi();
  const api = useRef(
    createMockApi(() => pricingRef.current, widgetId ? { solar: solarPreviewBackend(consoleApi, widgetId) } : undefined),
  ).current;
  const solar = product === "solar";
  const mode = branding.layout.mode;
  // Hosted pages run the Full calculator too (see styleOf in widget/src/render.ts)
  const isFullStyle = (b: WidgetBranding) => b.layout.style === "full" || b.layout.style === "hosted";
  const full = isFullStyle(branding);
  // Contact forms have their own renderer and screens (form, thank you)
  const contact = branding.layout.style === "contact";
  // The step bar lists whichever widget style is showing
  const steps: [AnyStep, string][] = solar
    ? SOLAR_STEPS.map((s) => [s, SOLAR_STEP_LABELS[s]])
    : contact
    ? CONTACT_STEPS
    : full
      ? FULL_STEPS.map((s) => [s, FULL_LABELS[s]])
      : STEPS.map((s) => [s, STEP_LABELS[s]]);

  // (Re)mount when the host element changes: layout mode, device, or a restart
  useEffect(() => {
    if (!hostRef.current) return;
    // product / mapsKey only matter for solar (mountWidget sends it to the solar widget)
    const options: MountOptions = {
      branding: brandingRef.current,
      api,
      contained: true,
      highlight: true,
      startOpen: true,
      onStep: setStep,
      product,
      mapsKey: MAPS_KEY,
    };
    const handle = mountWidget(hostRef.current, options);
    handleRef.current = handle;
    setStep(product === "solar" ? "address" : brandingRef.current.layout.style === "contact" ? "form" : isFullStyle(brandingRef.current) ? "location" : "postcode");
    return () => {
      handle.destroy();
      handleRef.current = null;
    };
  }, [mode, device, mountKey, full, contact, product]);

  // Restyle in place on every edit, keeping the current step
  useEffect(() => {
    handleRef.current?.update(branding);
  }, [branding]);

  function jump(s: AnyStep) {
    setStep(s);
    void handleRef.current?.goTo(s);
  }

  return (
    <div className="pv">
      {/* Toolbar: device (and restart) on the first row, the steps below */}
      <div className="pv-toolbar">
        <div className="pv-toolbar-row">
        <div className="ed-segmented" role="radiogroup" aria-label="Preview size">
          <button type="button" role="radio" aria-checked={device === "desktop"} className={device === "desktop" ? "is-on" : ""} onClick={() => setDevice("desktop")}>
            <Monitor size={14} aria-hidden="true" /> Desktop
          </button>
          <button type="button" role="radio" aria-checked={device === "phone"} className={device === "phone" ? "is-on" : ""} onClick={() => setDevice("phone")}>
            <Smartphone size={14} aria-hidden="true" /> Phone
          </button>
        </div>
        <button type="button" className="icon-btn" title="Restart preview" aria-label="Restart preview" onClick={() => setMountKey((k) => k + 1)}>
          <RotateCcw size={15} />
        </button>
        </div>
        <div className="pv-steps" role="tablist" aria-label="Calculator step">
          {steps.map(([s, name], i) => (
            <button key={s} type="button" role="tab" aria-selected={step === s} className={step === s ? "is-on" : ""} onClick={() => jump(s)}>
              {i + 1}. {name}
            </button>
          ))}
        </div>
      </div>

      {/* Browser window with a mock installer website */}
      <div className={`pv-window pv-${device}`}>
        <div className="pv-chrome" aria-hidden="true">
          <span />
          <span />
          <span />
          <div className="pv-url">www.your-heating-company.co.uk</div>
        </div>
        <div className="pv-viewport">
          {/* The installer's page as a grey wireframe (X boxes = images, bars = text),
              so the only thing in colour is the widget itself */}
          <div className="pv-site">
            <header className="pv-site-nav" aria-hidden="true">
              <span className="wf-box wf-logo" />
              <nav>
                <span className="wf-line wf-w60" />
                <span className="wf-line wf-w60" />
                <span className="wf-line wf-w60" />
                <span className="wf-pill" />
              </nav>
            </header>
            <section className="pv-site-hero" aria-hidden="true">
              <span className="wf-line wf-h-title wf-w70" />
              <span className="wf-line wf-w90" />
              <span className="wf-line wf-w60" />
            </section>
            <section className="pv-site-section">
              {/* Where the installer pastes the snippet's <div>: highlighted */}
              {mode === "inline" ? (
                <div className="pv-widget-slot">
                  <span className="pv-widget-tag">{contact ? "Your contact form" : solar ? "Your solar estimate" : "Your calculator"}</span>
                  <div ref={hostRef} key={`inline-${device}-${mountKey}`} />
                </div>
              ) : (
                <div className="pv-site-copy" aria-hidden="true">
                  <span className="wf-line wf-w90" />
                  <span className="wf-line wf-w80" />
                  <span className="wf-line wf-w50" />
                </div>
              )}
            </section>
            <section className="pv-site-section pv-site-cards" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <div key={i}>
                  <span className="wf-box wf-card-img" />
                  <span className="wf-line wf-h-sub wf-w60" />
                  <span className="wf-line wf-w90" />
                  <span className="wf-line wf-w70" />
                </div>
              ))}
            </section>
            <footer className="pv-site-foot" aria-hidden="true">
              <span className="wf-line wf-w30" />
            </footer>
          </div>
          {/* Floating widgets pin to the window, above the scrolling page.
              The renderer's highlight option outlines and labels the widget itself. */}
          {mode === "floating" && <div ref={hostRef} key={`floating-${device}-${mountKey}`} className="pv-floating-host" />}
        </div>
      </div>
    </div>
  );
}
